import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import {
  EmptyState,
  PageHeader,
  StatusBadge,
  TWButton,
  TWDataTable,
} from '../../components/ui';
import { DataTableSkeleton, StackedText, colTitle } from '../../data/columns';
import fleetApi from '../../services/fleetApi';
import AdminOnly from './components/admin-only';
import DevicesNav from './components/devices-nav';
import RolloutCreateModal from './components/rollout-create-modal';
import { pageOf, writePage } from './components/device-tab-utils';
import useFleetPoll, { fleetErrorText } from './use-fleet-poll';
import { APPS, PAGE_SIZE, POLL_MS, statusLabel } from './fleet-utils';

export const APP_TONES = { kiosk: 'blue', kitchen: 'violet', agent: 'slate' };
export const ROLLOUT_TONES = {
  running: 'sky',
  paused: 'amber',
  done: 'emerald',
  failed: 'rose',
  cancelled: 'slate',
};
const DASH = '—';

const SKELETON_HEADERS = [
  { label: 'Release', w: 'w-32' },
  { label: 'Status', shape: 'pill', w: 'w-20' },
  { label: 'Progress', w: 'w-28' },
  { label: 'Batch', w: 'w-24' },
  { label: 'Created', w: 'w-36' },
];

export const appLabel = (app) => APPS.find((a) => a.value === app)?.label || app;

const Progress = ({ counts }) => {
  const total = counts?.total ?? 0;
  const done = counts?.done ?? 0;
  const failed = counts?.failed ?? 0;
  const donePct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  const failedPct = total > 0 ? Math.min(100 - donePct, (failed / total) * 100) : 0;
  return (
    <div className="w-24 space-y-1">
      <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        <div className="h-full bg-emerald-500" style={{ width: `${donePct}%` }} />
        <div className="h-full bg-rose-500" style={{ width: `${failedPct}%` }} />
      </div>
      <p className="whitespace-nowrap text-xs font-medium tabular-nums text-slate-700 dark:text-slate-200">
        <span>{`${done}/${total}`}</span>
        {failed > 0 ? (
          <span className="text-rose-600 dark:text-rose-400">{`, ${failed} failed`}</span>
        ) : null}
      </p>
    </div>
  );
};

const Rollouts = () => (
  <AdminOnly>
    <RolloutsList />
  </AdminOnly>
);

const RolloutsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = pageOf(searchParams);

  const { data, loading, fetching, error, stalled, reload } = useFleetPoll(
    () =>
      fleetApi
        .listRollouts('?pageNumber=' + page + '&pageSize=' + PAGE_SIZE)
        .then((res) => ({ rows: res.data.data || [], totalRecords: res.data.totalRecords ?? 0 })),
    {
      intervalMs: POLL_MS,
      deps: [page],
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load rollouts')),
    },
  );
  const rows = data?.rows || [];
  const totalRecords = data?.totalRecords ?? 0;
  const pastTheEnd = !loading && !error && rows.length === 0 && totalRecords > 0 && page > 1;
  useEffect(() => {
    if (pastTheEnd) writePage(setSearchParams, 1);
  }, [pastTheEnd, setSearchParams]);

  // The Devices list hands its filtered ids over in location.state. They move
  // into modal state first, then the entry is replaced so a refresh or Back
  // does not open the modal again.
  const [modal, setModal] = useState({ open: false, deviceIds: [] });
  useEffect(() => {
    const ids = location.state?.deviceids;
    if (Array.isArray(ids) && ids.length > 0) {
      setModal({ open: true, deviceIds: ids });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const openModal = () => setModal({ open: true, deviceIds: [] });
  const closeModal = () => setModal((prev) => ({ ...prev, open: false }));

  const columns = [
    {
      title: colTitle('Release'),
      key: 'release',
      render: (_, r) => (
        <div className="flex items-center gap-2">
          <StatusBadge size="sm" tone={APP_TONES[r.app] || 'slate'} label={appLabel(r.app)} />
          <StackedText
            primary={r.release?.versionname || DASH}
            secondary={r.release ? `code ${r.release.versioncode}` : undefined}
          />
        </div>
      ),
    },
    {
      title: colTitle('Status'),
      key: 'status',
      render: (_, r) => (
        <StatusBadge
          dot
          size="sm"
          tone={ROLLOUT_TONES[r.status] || 'slate'}
          label={statusLabel(r.status)}
        />
      ),
    },
    {
      title: colTitle('Progress'),
      key: 'progress',
      render: (_, r) => <Progress counts={r.counts} />,
    },
    {
      title: colTitle('Batch'),
      key: 'batch',
      render: (_, r) => (
        <span className="whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">
          {`${r.batchsize} every ${r.batchintervalminutes} min`}
        </span>
      ),
    },
    {
      title: colTitle('Created'),
      key: 'created',
      render: (_, r) => (
        <StackedText
          primary={r.createdbyemail || DASH}
          secondary={r.createdate ? dayjs(r.createdate).format('MMM D, YYYY HH:mm') : DASH}
        />
      ),
    },
  ];

  let body;
  if (error) {
    body = (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load rollouts"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  } else if (loading || pastTheEnd) {
    body = <DataTableSkeleton headers={SKELETON_HEADERS} rows={6} />;
  } else if (totalRecords === 0 && rows.length === 0) {
    body = (
      <EmptyState
        icon={RocketLaunchIcon}
        title="No rollouts yet"
        description="A rollout installs a release on devices in small batches."
        action={
          <TWButton variant="primary" onClick={openModal}>
            New rollout
          </TWButton>
        }
      />
    );
  } else {
    body = (
      <div className="reveal">
        {stalled ? (
          <div
            role="status"
            className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
          >
            <span>Live updates paused.</span>
            <TWButton variant="link" size="sm" onClick={reload}>
              Retry
            </TWButton>
          </div>
        ) : null}
        <TWDataTable
          columns={columns}
          data={rows}
          loading={fetching}
          rowClickPath={(r) => `/dashboard/devices/rollouts/${r.id}`}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: totalRecords,
            showSizeChanger: false,
            showTotal: (t) => `${t} ${t === 1 ? 'rollout' : 'rollouts'}`,
            onChange: (next) => writePage(setSearchParams, next),
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title="Rollouts"
          description="Staged installs, a few devices at a time."
          actions={
            <TWButton variant="primary" onClick={openModal}>
              New rollout
            </TWButton>
          }
        />
        <DevicesNav current="rollouts" />
        {body}
      </div>

      <RolloutCreateModal
        open={modal.open}
        onClose={closeModal}
        initialDeviceIds={modal.deviceIds}
        onCreated={(rollout) => {
          closeModal();
          navigate(`/dashboard/devices/rollouts/${rollout.id}`);
        }}
      />
    </div>
  );
};

export default Rollouts;
